import React, {
  Suspense,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import Widget from 'designer/display/widget'
import Loader from 'components/Loader/Loader'
import { useRouteMatch, useLocation } from 'react-router-dom'

// Action
import { widgetUischema, widgetSchema } from 'designer/component/action/Schema'
import { createDesignerData } from 'designer/component/action/Design'
import { debounceCallback } from 'utility/debounceCallback.util'
import { onUpdateCondition } from '../action/condition/Action'
import { getCurrentUi } from '../action/Data'

const WidgetComponent = (props) => {
  let { uischema, action, config } = props
  let {
    isDynamic = false,
    dynamicData,
    dynamicIndex,
    dynamicName,
    dynamicKey,
  } = config || {}

  const designContext = useContext(DesignContext)
  const {
    jsonForm,
    setFormData,
    setFieldFormData,
    setFieldAllData,
    setFieldSearchData,
    setDesignerPropsData,
    setOpenModal,
    openModal,
    modalForm,
    isSyncWidget,
    setIsSyncWidget,
    setWidgetComponent,
    searchData,
    allData,
    setAllData,
    elementAction,
    isInputSearch,
    widgetDesign,
    isUiChanged,
    setIsUiChanged,
    employeeList,
  } = designContext

  const match = useRouteMatch()
  const location = useLocation()

  let search = location.search || []
  let pathParams = match.params
  let urlSearchParams = new URLSearchParams(search)
  let queryParams = Object.fromEntries([...urlSearchParams])
  let perspectiveRoute = match.params.perspective || ''
  let queryString = JSON.stringify(queryParams)

  let designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
  const widgetName = uischema.name
  const widgetKey = uischema.key
  const designerKey = widgetKey
  const widgetCode = uischema.path
  const isGroupValue = uischema.isGroupValue
  const designerProps = objectPath.get(designerPropsData, `props`) || []
  const favoriteProps = objectPath.get(designerPropsData, `favoriteProps`) || []
  const allDesignerProps = [...designerProps, ...favoriteProps]
  const isReset = objectPath.get(designerPropsData, `isReset`) || false
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  const isWidgetUpdate = objectPath.get(isUiChanged, [widgetKey, 'isRender'])
  const otherParamUpdated = objectPath.get(isUiChanged, 'otherParamUpdated')

  const tempWidget =
    objectPath.get(isUiChanged, ['widgetList', designerKey]) || []

  let currentWidgetData = uischema.widget
  let widgetRevisionId = currentWidgetData?.formDesign?.formRevisionId
  let widgetJsonForm = objectPath.get(widgetDesign, widgetCode)
  const [widgetData, setWidgetData] = useState(null)
  const [render, setRender] = useState(null)
  const [dataChange, setDataChange] = useState({})
  const [isDataUpdate, setIsDataUpdate] = useState({})

  const formData = useMemo(() => {
    if (openModal && modalForm) {
      return modalForm?.formData
    }
    return jsonForm?.formData
  }, [openModal])

  useEffect(() => {
    if (!tempWidget) {
      let receiveProp = objectPath.get(currentWidgetData, 'receiveProp')
      setWidgetComponent(designerKey, {
        formRevisionId: widgetRevisionId,
        receiveProp: receiveProp || [],
      })
      setWidgetData(widgetJsonForm)
    }
  }, [])

  useEffect(async () => {
    if (isSyncWidget) {
      setWidgetData({
        uischema: widgetUischema,
        schema: widgetSchema,
      })
      setIsSyncWidget(false)
      setRender(true)
    }
  }, [isSyncWidget])

  useEffect(() => {
    if (render) {
      setWidgetData(widgetJsonForm)
      setRender(false)
    }
  }, [render])

  const onUpdateData = (data = {}) => {
    allDesignerProps.forEach(
      ({
        systemType,
        systemKey,
        prop,
        system = [],
        isDynamic,
        dynamicName,
      }) => {
        if (!objectPath.has(data, prop)) return
        if (isDynamic) {
          let value = objectPath.get(data, `${dynamicName}`)
          objectPath.set(formData, dynamicName, value)
        } else if (systemType === 'system' || systemType === 'input') {
          let value = objectPath.get(data, prop)
          objectPath.set(formData, systemKey, value)
        } else {
          system.forEach(({ systemKey, field, isDynamic }) => {
            if (isDynamic) {
              let value = objectPath.get(data, `${prop}`)
              objectPath.set(formData, field, value)
            } else {
              let value = objectPath.get(data, `${prop}.${field}`)
              objectPath.set(formData, systemKey, value)
              objectPath.set(formData, field, value)
            }
          })
        }
      }
    )
    let props = jsonForm?.designerPropsData?.props || []
    let sendProps = props.filter(
      ({ propReceive, propSend, elementKey }) =>
        !propReceive && propSend && elementKey === uischema.key
    )
    let widgetData = objectPath.get(
      isUiChanged,
      `widgetList.${uischema.key}.formData`
    )
    sendProps.forEach(({ propKey, systemKey, systemType }) => {
      let value = objectPath.get(widgetData, propKey)
      if (systemType === 'input') {
        let ui = getCurrentUi(systemKey, jsonForm.schema, jsonForm.uischema)
        if (ui.key === systemKey) {
          objectPath.set(formData, ui.name, value)
        }
        objectPath.set(formData, systemKey, value)
      }
    })
    setFormData(formData)
  }

  const onUpdateFormData = (key, data, isSearch) => {
    if (key) {
      if (isSearch) {
        setFieldSearchData(uischema, `${widgetName}.${key}`, data)
        setFieldSearchData(uischema, key, data)
      }
      setFieldFormData(`${widgetName}.${key}`, data)
      setFieldFormData(key, data)
      setFieldAllData(`${widgetName}.${key}`, data)
      setFieldAllData(key, data)
    } else {
      data = data || {}
      setFormData({ ...formData, ...data })
      setAllData({ ...allData, ...data })
    }
    debounceCallback(
      onUpdateCondition,
      [
        props,
        designContext,
        widgetKey,
        data,
        { isOnChange: true, isWidget: true, widgetName, widgetKey },
      ],
      `onUpdateWidget-${widgetKey}`,
      500
    )
  }

  const onCloseModal = () => {
    objectPath.set(designerPropsData, 'modal', null)
    setDesignerPropsData(designerPropsData)
    setOpenModal(null)
  }

  const onClearReset = () => {
    objectPath.set(designerPropsData, 'isReset', false)
    setDesignerPropsData(designerPropsData)
  }

  const [dataUpdate, setDataUpdate] = useState({})

  useEffect(() => {
    let formData = jsonForm.formData || {}
    if (uischema.name) {
      if (otherParamUpdated) {
        objectPath.del(isUiChanged, 'otherParamUpdated')
        setIsUiChanged(isUiChanged)
      } else if (isDynamic) {
        if (dynamicData) {
          allDesignerProps.forEach((item) => {
            if (item.elementKey !== uischema.key) return
            if (item.dynamicName !== dynamicName) return
            let itemKey = `${uischema.name}.${dynamicName}.${dynamicIndex}.${
              item.itemFullKey || item.propKey
            }`
            if (item.systemType === 'dynamicData') {
              let value = objectPath.get(dynamicData, item.systemName)
              objectPath.set(formData, itemKey, value)
            } else if (item.systemType === 'custom') {
              let value = item.systemValue
              if (item.customType === 'number') {
                value = Number(value)
              } else if (
                item.customType === 'array' ||
                item.customType === 'object'
              ) {
                if (typeof systemValue === 'string') {
                  value = JSON.parse(value)
                }
              }
              objectPath.set(formData, itemKey, value)
            }
          })
        }
      } else {
        allDesignerProps.forEach((item) => {
          if (item.elementKey !== uischema.key) return
          if (item.systemType === 'input') {
            let value = objectPath.get(formData, item.systemKey)
            objectPath.set(formData, `${uischema.name}.${item.propKey}`, value)
            objectPath.set(
              formData,
              `${uischema.name}.${item.fullPropKey}`,
              value
            )
          } else if (item.systemType === 'custom') {
            let value = item.systemValue
            if (item.customType === 'number') {
              value = Number(value)
            } else if (
              item.customType === 'array' ||
              item.customType === 'object'
            ) {
              if (typeof systemValue === 'string') {
                value = JSON.parse(value)
              }
            }
            objectPath.set(formData, `${uischema.name}.${item.propKey}`, value)
            objectPath.set(
              formData,
              `${uischema.name}.${item.fullPropKey}`,
              value
            )
          }
        })
      }
    }
    setDataUpdate(formData)
  }, [!isDynamic && JSON.stringify(jsonForm.formData), otherParamUpdated])

  useEffect(() => {
    objectPath.set(isDataUpdate, [widgetName], true)
    setIsDataUpdate(isDataUpdate)
  }, [JSON.stringify(objectPath.get(dataUpdate, widgetName)), location.search])

  return (
    <Suspense fallback={<Loader />}>
      <Widget
        currentWidget={widgetJsonForm}
        isSyncWidget={isSyncWidget}
        uiWidgetKey={uischema.key}
        externalCode={widgetCode}
        externalRevisionId={widgetRevisionId}
        designerConfig={config}
        dataUpdate={dataUpdate}
        formData={formData}
        isDataUpdate={isDataUpdate}
        setIsDataUpdate={setIsDataUpdate}
        designerData={
          objectPath.has(dataUpdate, widgetName)
            ? objectPath.get(dataUpdate, widgetName)
            : dataUpdate
        }
        widgetData={widgetData}
        designerProps={allDesignerProps}
        designerKey={designerKey}
        onUpdateData={onUpdateData}
        onUpdateFormData={onUpdateFormData}
        onCloseModal={onCloseModal}
        isReset={isReset}
        onClearReset={onClearReset}
        searchApi={jsonForm.search}
        searchData={searchData}
        isLocked={isLocked}
        isInputSearch={isInputSearch}
        isGroupValue={isGroupValue}
        widgetName={widgetName}
        widgetKey={designerKey}
        isWidgetUpdate={isWidgetUpdate}
        employeeList={employeeList}
      />
    </Suspense>
  )
}

export default WidgetComponent
